/**
 * Password-reset action creators (AUTH-09 / AUTH-10 / D-10).
 *
 * requestReset: ALWAYS resolves void. Backend returns 202 for known emails
 * AND 202 for unknown emails (enumeration defense per AUTH-09). UI shows an
 * identical "check your email" state regardless.
 *
 * completeReset: resolves on success; throws ApiError-shaped on RESET_TOKEN_*
 * errors. D-10 mandates NO auto-login — backend revokes all refresh families
 * on reset, so surfaces always route to /login post-success.
 */
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";

export interface RequestResetArgs {
  email: string;
  tenantSlug: string;
  apiBaseUrl: string;
  /**
   * Reset-link destination (CC-214): `"mobile"` → `/me/auth/reset`,
   * `"admin"` → `/admin/auth/reset`, `"accounting"` → `/accounting/auth/reset`.
   * Omitted → backend defaults to `/me`. The token itself stays
   * audience-agnostic; this only routes the link.
   */
  audience?: "admin" | "mobile" | "accounting";
}

export async function requestReset({
  email,
  tenantSlug,
  apiBaseUrl,
  audience,
}: RequestResetArgs): Promise<void> {
  await fetch(apiBaseUrl + "/api/v1/auth/password-reset/request", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({ email, tenantSlug, ...(audience ? { audience } : {}) }),
  });
  // Per AUTH-09: always resolve void. Response is 202 for both known + unknown
  // emails. UI never branches on status.
}

export interface CompleteResetArgs {
  token: string;
  newPassword: string;
  apiBaseUrl: string;
}

export async function completeReset({
  token,
  newPassword,
  apiBaseUrl,
}: CompleteResetArgs): Promise<void> {
  const res = await fetch(apiBaseUrl + "/api/v1/auth/password-reset/complete", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAcceptLanguageHeader() },
    body: JSON.stringify({ token, newPassword }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      errorCode?: string;
      title?: string;
    };
    throw Object.assign(new Error(body.title ?? "Reset failed"), {
      status: res.status,
      errorCode: body.errorCode ?? "RESET_TOKEN_INVALID",
      correlationId: res.headers.get("x-correlation-id"),
      isApiError: true,
    });
  }
}
